/** A comma-separated data attribute as the list of line keys it stands for. */
function keys(value) {
  return (value || '').split(',').filter(Boolean);
}

/*
 * Failed automatic claims in this page view, per variant. Lives outside the
 * class because every re-render builds a new element, and the count has to
 * survive that — otherwise a gift that cannot be added would be retried on
 * every single cart change. See autoClaim().
 */
const autoClaimFailures = new Map();
const AUTO_CLAIM_RETRIES = 2;

/*
 * Automatic claims currently in flight, by variant.
 *
 * `data-busy` only ever guarded one element, and every re-render builds a new
 * one — which was survivable while the drawer was the only caller, because its
 * whole section is replaced in one go. On the cart page two quick changes (the
 * quantity stepper debounces, the remove button does not) produce two
 * `cart:updated` in a row, two refreshes of the milestone block, and both come
 * back still carrying data-auto-variant because the first add has not landed
 * yet. Two adds of the same variant merge into a line of two, the Cart
 * Transform refuses anything but one, and the customer pays for the gift.
 *
 * Keyed by variant rather than a single flag: the entry is only cleared once
 * the add has answered, and by then the cart holds the line, so the element
 * that renders next is not offered the claim again in the first place.
 */
const autoClaimInFlight = new Set();

/*
 * Holds the checkout button's spinner while a gift is being added or taken
 * out (the counter lives in assets/cart.js). Without it the spinner stopped as
 * soon as the quantity change itself was through, and the cart showed the new
 * total with no gift in it for a few seconds — long enough to click through.
 * Registered the moment a request starts: the element is built inside
 * cart.js's re-render, so this always lands before cart.js lets go.
 */
function holdCheckout(isBusy) {
  if (window.theme && typeof theme.setCheckoutLoading === 'function') theme.setCheckoutLoading(isBusy);
}

/*
 * Same swap as assets/cart.js: theme.morph (assets/dom-morph.js) lines the
 * existing markup up with the new one, so a gift coming or going no longer
 * rebuilds the whole drawer. This element itself is still replaced whole —
 * it is on dom-morph's replace list.
 */
function swapContents(target, html) {
  if (window.theme && typeof theme.morph === 'function') {
    theme.morph(target, html);
  } else {
    target.innerHTML = html;
  }
}

/*
 * Claims the milestone reward.
 *
 * Adds the chosen variant(s) with the `_milestone_reward` line property,
 * which is what the Discount Function requires before it will discount
 * anything — a product added any other way stays at full price.
 *
 * Normally the customer has to pick. The exception is a gift step with a
 * single product on it: Liquid marks that with data-auto-variant and
 * autoClaim() puts it in the cart without anyone clicking. A removed reward
 * still stays removed — see autoClaim for the cart attribute that remembers
 * which gifts have already been handed out.
 *
 * When the cart drops back below the threshold, Liquid renders this element
 * with data-expired-keys instead of the picker, and those lines are taken out
 * here.
 */
class CartMilestonePicker extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.handleClick.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);
    this.onDialogClick = (event) => event.stopPropagation();
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    // openDialog() moves the <dialog> to <body> (see the comment there), which
    // takes it out of this element's subtree — a listener on `this` alone
    // would stop catching clicks inside it the moment it moves. Binding
    // directly to the dialog now, while it is still a child, keeps working
    // no matter where in the tree it ends up later.
    // Held as its own reference rather than looked up again on the way out:
    // the dialog travels to <body> and back, so by the time this element is
    // discarded `this.dialog` may well find nothing to detach from.
    this.boundDialog = this.dialog;
    this.boundDialog?.addEventListener('click', this.onClick);
    // The dialog spends its whole open life as a child of <body> (see
    // openDialog), which puts every click inside it — a tile, the close
    // button, the backdrop — outside <cart-drawer>. MenuDrawer.openMenuDrawer
    // (assets/global.js) listens for clicks on document.body and closes the
    // drawer on any whose target it does not contain, so picking a gift used
    // to shut the cart behind the picker. Stopping those clicks short of body
    // fixes it without touching the shared drawer class — this sits on the
    // same element as the handler above, so both still run.
    this.boundDialog?.addEventListener('click', this.onDialogClick);
    // See handleKeyUp for why this stays attached permanently.
    this.addEventListener('keyup', this.onKeyUp);

    // Housekeeping Liquid worked out for us: lines whose milestone rung the
    // cart no longer clears go, and a line that ended up holding more than one
    // unit comes back to one. Both silent — the customer never asked for
    // either. See snippets/cart-milestone-picker.liquid for how a line ends up
    // in each list.
    //
    // One request for both: cart/update takes a whole map, and firing two
    // would have the data-busy guard swallow the second.
    const updates = {};
    keys(this.dataset.expiredKeys).forEach((key) => {
      updates[key] = 0;
    });
    keys(this.dataset.trimKeys).forEach((key) => {
      // An expired line is on its way out; trimming it to one would put it
      // back. Removal wins.
      if (updates[key] !== 0) updates[key] = 1;
    });

    // Auto memory Liquid wants written back — gifts whose rung the cart no
    // longer clears, dropped from the list so they can be handed out again on
    // the way up. Rides along with the line updates above: same request, same
    // re-render.
    const attributes = {};
    if (this.dataset.autoMemory !== undefined) attributes._milestone_auto = this.dataset.autoMemory;
    // Rides along with whatever else is being written: see the healing block in
    // snippets/cart-milestone-picker.liquid. Once this sits in the cart, a gift
    // the customer took out stays out again, exactly as before.
    if (this.dataset.autoMigrate) attributes._milestone_auto_v2 = this.dataset.autoMigrate;

    if (Object.keys(updates).length || Object.keys(attributes).length) {
      // Deliberately not followed by an auto-claim in the same pass: the
      // re-render this triggers builds a fresh element out of the corrected
      // cart, and that one claims. Firing both here would have the data-busy
      // guard swallow the second.
      this.applyLineUpdates(updates, false, attributes);
      return;
    }

    if (this.dataset.autoVariant) this.autoClaim();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keyup', this.onKeyUp);
    this.boundDialog?.removeEventListener('click', this.onClick);
    this.boundDialog?.removeEventListener('click', this.onDialogClick);
  }

  get miniCart() {
    return document.querySelector('mini-cart');
  }

  get dialog() {
    return this.querySelector('[data-milestone-dialog]');
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // A step with a single option renders no dialog at all (see the snippet):
    // the button carries the variant and claiming is all it does.
    const direct = target.closest('[data-milestone-claim]');
    if (direct) {
      event.preventDefault();
      this.claim([direct]);
      return;
    }

    const open = target.closest('[data-milestone-open]');
    if (open) {
      event.preventDefault();
      this.openDialog(open);
      return;
    }

    if (target.closest('[data-milestone-close]')) {
      event.preventDefault();
      this.openDialogEl?.close();
      return;
    }

    // A click landing on the <dialog> element itself (not its content) is a
    // backdrop click — the dialog has no padding of its own, so its box is
    // exactly the backdrop area once open.
    if (this.openDialogEl && target === this.openDialogEl) {
      this.openDialogEl.close();
      return;
    }

    const confirm = target.closest('[data-milestone-confirm]');
    if (confirm) {
      event.preventDefault();
      const dialog = this.openDialogEl;
      if (!dialog) return;
      const selected = Array.from(dialog.querySelectorAll('[data-milestone-choose][data-selected]'));
      if (selected.length) this.claim(selected);
      return;
    }

    const choose = target.closest('[data-milestone-choose]');
    if (choose) {
      event.preventDefault();
      const dialog = this.openDialogEl;
      const maxPicks = dialog ? parseInt(dialog.dataset.maxPicks, 10) || 1 : 1;
      if (maxPicks > 1) {
        this.toggleTile(choose, dialog, maxPicks);
      } else {
        this.claim([choose]);
      }
    }
  }

  /*
   * A <dialog> shown with showModal() centers itself with position: fixed
   * against the nearest transformed ancestor, not the viewport — the
   * drawer's own slide-in transform would otherwise pin it to the drawer's
   * own box instead of the screen. Moving it to <body> while open sidesteps
   * that; closeDialog() puts it back, which is harmless even if that spot no
   * longer exists (a claim re-renders the whole drawer).
   *
   * That move takes focus out of <cart-drawer>'s own DOM subtree for the
   * instant showModal() runs (focus lands inside the relocated dialog).
   * <cart-drawer> (assets/global.js, MenuDrawer.onFocusOut) treats any focus
   * leaving its subtree as "closed by the customer" and closes itself —
   * without this guard, opening the picker would silently close the drawer
   * behind it. Swallowing just that one focusout here, right as it happens,
   * fixes it without touching the shared drawer class.
   */
  openDialog(trigger) {
    const dialog = this.dialog;
    if (!dialog) return;
    this.trigger = trigger;
    this.dialogHomeParent = dialog.parentNode;
    this.dialogHomeNextSibling = dialog.nextSibling;
    document.body.appendChild(dialog);
    this.openDialogEl = dialog;
    dialog.addEventListener('close', () => this.closeDialog(dialog), { once: true });

    this.addEventListener('focusout', (event) => event.stopPropagation(), { once: true });
    dialog.showModal();
    // Off the close button, which showModal() would otherwise autofocus and
    // the browser would draw the keyboard focus ring around before the
    // customer has touched a key. The dialog carries tabindex="-1" for this.
    dialog.focus({ preventScroll: true });
  }

  closeDialog(dialog) {
    if (this.dialogHomeParent && this.dialogHomeParent.isConnected) {
      this.dialogHomeParent.insertBefore(dialog, this.dialogHomeNextSibling);
    } else if (dialog.isConnected) {
      dialog.remove();
    }
    if (this.openDialogEl === dialog) this.openDialogEl = null;
    this.trigger?.focus();
    // See handleKeyUp: focus returning to a still-open <details> makes the
    // very next Escape keyup look like it targeted the drawer, not the
    // dialog that just consumed it.
    this.justClosedDialog = true;
    setTimeout(() => {
      this.justClosedDialog = false;
    }, 100);
  }

  /*
   * Native <dialog> closes on Escape before this component sees anything —
   * by the time the keyup for that same key release fires, focus has
   * already moved back to the trigger button, which sits inside the
   * drawer's own open <details>. <cart-drawer>'s onKeyUp (assets/global.js)
   * reads that as "Escape was meant for the drawer" and closes it too.
   * Swallowing only the keyup that immediately follows a dialog close (see
   * closeDialog's justClosedDialog) leaves every other Escape — including a
   * customer genuinely closing the drawer — untouched.
   */
  handleKeyUp(event) {
    if (!this.justClosedDialog) return;
    this.justClosedDialog = false;
    const key = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
    if (key === 'ESCAPE') event.stopPropagation();
  }

  toggleTile(button, dialog, maxPicks) {
    const selected = dialog.querySelectorAll('[data-milestone-choose][data-selected]');

    if (button.hasAttribute('data-selected')) {
      button.removeAttribute('data-selected');
    } else {
      if (selected.length >= maxPicks) return;
      button.setAttribute('data-selected', '');
    }

    this.updateTileState(dialog, maxPicks);
  }

  updateTileState(dialog, maxPicks) {
    const tiles = Array.from(dialog.querySelectorAll('[data-milestone-choose]'));
    const selectedCount = tiles.filter((tile) => tile.hasAttribute('data-selected')).length;
    const atMax = selectedCount >= maxPicks;

    tiles.forEach((tile) => {
      if (tile.hasAttribute('data-unavailable')) return;
      tile.disabled = !tile.hasAttribute('data-selected') && atMax;
    });

    const confirmButton = dialog.querySelector('[data-milestone-confirm]');
    if (confirmButton) confirmButton.disabled = selectedCount === 0;
  }

  /*
   * The sections every cart write has to bring back.
   *
   * The drawer used to be the only place this component rendered, so the list
   * came from <mini-cart>. It renders on the cart page too now (see
   * sections/main-cart-footer.liquid), and that page has no <mini-cart> at
   * all — asking for the list there gave back nothing, the cart write went
   * through unrendered, and the claimed gift only appeared after a manual
   * reload. Built from the containers the page actually has instead, the way
   * assets/cart.js does it: `section` is what the Section Rendering API is
   * asked for, `id` is the element it goes back into.
   */
  sectionsToRender() {
    const miniCart = this.miniCart;
    if (miniCart) return miniCart.getSectionsToRender();

    const sections = [];
    const items = document.getElementById('main-cart-items');
    if (items) sections.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    // The whole footer rather than its `.js-contents`: that wrapper sits
    // around the subtotal block alone, and the milestone bar hangs above it.
    const footer = document.getElementById('main-cart-footer');
    if (footer) sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '#main-cart-footer' });
    ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach((id) => {
      if (document.getElementById(id)) sections.push({ id, section: id, selector: '.shopify-section' });
    });
    return sections;
  }

  sections() {
    return this.sectionsToRender()
      .map((section) => section.section)
      .filter(Boolean);
  }

  claim(buttons) {
    const variantIds = buttons.map((button) => button.dataset.variantId).filter(Boolean);
    if (!variantIds.length || this.hasAttribute('data-busy')) return;

    this.setAttribute('data-busy', '');
    holdCheckout(true);
    buttons.forEach((button) => button.setAttribute('disabled', 'disabled'));

    // Hidden from the customer in cart and checkout by the leading underscore.
    const itemPayload =
      variantIds.length === 1
        ? { id: variantIds[0], quantity: 1, properties: { _milestone_reward: '1' } }
        : { items: variantIds.map((id) => ({ id, quantity: 1, properties: { _milestone_reward: '1' } })) };

    const body = JSON.stringify({
      ...itemPayload,
      sections: this.sections(),
      sections_url: window.location.pathname,
    });

    fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status === 422) {
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } }),
          );
          return;
        }
        // The drawer's own re-render below replaces the whole subtree the
        // dialog used to live in — close it explicitly first, or a dialog
        // moved to <body> by openDialog() would stay stuck open.
        this.openDialogEl?.close();
        this.parkFocus();
        this.render(parsedState);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        this.removeAttribute('data-busy');
        holdCheckout(false);
        buttons.forEach((button) => button.removeAttribute('disabled'));
      });
  }

  /*
   * Claims a step that has nothing to choose about it: one gift, one product.
   * Liquid hands over the variant with data-auto-variant (see the snippet for
   * when it does and when it keeps the button instead).
   *
   * Two requests, because cart/add.js cannot set cart attributes and
   * cart/update.js cannot set line properties. The add runs without sections,
   * the attribute write asks for them, so there is still only one re-render.
   *
   * The memory used to be written whatever the add answered. That was the one
   * way a cart could end up saying "gift handed out" with no gift in it: a
   * single failed add — a rejected request, a hiccup, anything that is not a
   * cart back — and the step was closed for good, the customer saw the bar
   * reach the gift, and nothing was in the order. Now the memory is only
   * written once the cart really came back with the line, so a failure is
   * simply retried on the next render. AUTO_CLAIM_RETRIES caps that: after two
   * silent retries in one page view the memory goes in anyway and the manual
   * button takes over, which is what keeps a gift that genuinely cannot be
   * added from retrying on every cart change.
   *
   * Silent — no `cart:updated`. The customer did not ask for this, and the
   * sections that come back already carry the new cart bubble.
   */
  autoClaim() {
    const variantId = this.dataset.autoVariant;
    if (!variantId || this.hasAttribute('data-busy')) return;
    if ((autoClaimFailures.get(variantId) || 0) > AUTO_CLAIM_RETRIES) return;
    // Another element is already adding this exact gift — see autoClaimInFlight.
    if (autoClaimInFlight.has(variantId)) return;

    autoClaimInFlight.add(variantId);
    this.setAttribute('data-busy', '');
    holdCheckout(true);

    const addBody = JSON.stringify({
      id: variantId,
      quantity: 1,
      properties: { _milestone_reward: '1' },
    });

    fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body: addBody })
      .then((response) => response.json())
      .then((added) => {
        // cart/add.js answers with the line it created. Anything else — a 422
        // body carrying `description`, an error page parsed into something
        // without an id — means the gift is not in the cart.
        const ok = added && !added.status && added.id;
        if (!ok) {
          const failures = (autoClaimFailures.get(variantId) || 0) + 1;
          autoClaimFailures.set(variantId, failures);
          if (failures <= AUTO_CLAIM_RETRIES) return null;
        }

        const updateBody = JSON.stringify({
          // The flag marks this cart as one that never needed healing — it was
          // built after the fix, so its memory is trustworthy from here on.
          attributes: { _milestone_auto: this.dataset.autoMemoryNext || '', _milestone_auto_v2: 'done' },
          sections: this.sections(),
          sections_url: window.location.pathname,
        });

        return fetch(`${theme.routes.cart_update_url}`, { ...fetchConfig('javascript'), body: updateBody })
          .then((response) => response.json())
          .then((parsedState) => this.render(parsedState, false));
      })
      .catch((error) => console.error(error))
      .finally(() => {
        autoClaimInFlight.delete(variantId);
        this.removeAttribute('data-busy');
        holdCheckout(false);
      });
  }

  /*
   * close() hands focus back to the trigger button, which the re-render that
   * follows is about to destroy. Chrome drops focus to <body> silently, but
   * browsers that do fire focusout when the focused element is removed would
   * hand MenuDrawer.onFocusOut (assets/global.js) a drawer whose focus sits
   * outside it — and that closes the drawer too. The drawer's own <summary>
   * survives the re-render and is inside the <details> that check looks at,
   * so parking focus there keeps it satisfied everywhere.
   */
  parkFocus() {
    const summary = this.closest('cart-drawer')?.querySelector('summary');
    summary?.focus({ preventScroll: true });
  }

  /*
   * Applies a whole map of line key -> quantity in one go, optionally with
   * cart attributes alongside. cart/change.js only ever handles a single line,
   * which with a multi-pick step would mean one request and one re-render per
   * claimed item — visible flicker, and the cart briefly holding rewards it no
   * longer qualifies for. cart/update.js takes the map instead, and carries
   * the attributes in the same request.
   *
   * Never announced when it is housekeeping: nothing about the customer's own
   * cart changed as far as they are concerned, and a `cart:updated` would have
   * the drawer redraw itself over a correction they never saw go wrong.
   */
  applyLineUpdates(updates, announce, attributes = null) {
    const hasAttributes = attributes && Object.keys(attributes).length;
    if ((!Object.keys(updates).length && !hasAttributes) || this.hasAttribute('data-busy')) return;
    this.setAttribute('data-busy', '');
    holdCheckout(true);

    const body = JSON.stringify({
      updates,
      ...(hasAttributes ? { attributes } : {}),
      sections: this.sections(),
      sections_url: window.location.pathname,
    });

    fetch(`${theme.routes.cart_update_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => this.render(parsedState, announce))
      .catch((error) => console.error(error))
      .finally(() => {
        this.removeAttribute('data-busy');
        holdCheckout(false);
      });
  }

  /*
   * Swaps in the re-rendered sections. Deliberately not mini-cart's own
   * renderContents(): that also opens the drawer, which would be wrong when
   * this is silently cleaning up an expired reward.
   */
  render(parsedState, announce = true) {
    if (!parsedState.sections) return;

    this.sectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      const html = parsedState.sections[section.section];
      if (!element || !html) return;
      // The drawer hands over a #mini-cart that already holds the section's
      // own contents, so querySelector finds nothing and the element itself is
      // the target — same swap as before. The cart page's containers do wrap
      // their target, exactly as assets/cart.js expects.
      const target = element.querySelector(section.selector) || element;
      swapContents(target, this.getSectionInnerHTML(html, section.selector));
    });

    if (announce) {
      // `milestone` marks it as ours — see the document listener at the bottom.
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState, milestone: true } }));
    }
  }

  /** Same extraction <mini-cart> does, but without needing one on the page. */
  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }
}

if (!customElements.get('cart-milestone-picker')) {
  customElements.define('cart-milestone-picker', CartMilestonePicker);
}

/*
 * The cart page rewrites itself piecemeal. assets/cart.js swaps only the
 * containers it knows after a quantity change — the item rows, the totals,
 * the bar — and the picker stayed as it was: a gift whose rung the cart had
 * just dropped below sat on at full price with nothing around to take it out
 * until the next page load. Every one of those rewrites ends in a
 * `cart:updated`, so the milestone block (sections/main-cart-footer.liquid,
 * `[data-cart-milestone]`) fetches itself fresh on that event and the element
 * inside runs its housekeeping or auto-claim again. Events this component
 * raises itself carry `milestone` and are skipped: its render has already
 * swapped the whole footer.
 *
 * The drawer needs none of this — its section is replaced whole.
 */
let milestoneRefreshToken = 0;

document.addEventListener('cart:updated', (event) => {
  if (event.detail?.milestone) return;
  const wrapper = document.querySelector('[data-cart-milestone]');
  const footer = document.getElementById('main-cart-footer');
  if (!wrapper || !footer?.dataset.id) return;

  // Two changes in quick succession (the stepper debounces, the remove button
  // does not) leave two of these in the air at once, and whichever answers
  // last wins — which is not the same as the newest. An older answer can carry
  // a data-expired-keys for a gift the cart has meanwhile earned back. Only
  // the most recent request is allowed to write.
  const token = ++milestoneRefreshToken;
  // Until the fresh block is in, nobody knows yet whether a gift comes or goes.
  holdCheckout(true);

  fetch(`${window.location.pathname}?sections=${footer.dataset.id}`)
    .then((response) => response.json())
    .then((sections) => {
      if (token !== milestoneRefreshToken) return;
      const html = sections[footer.dataset.id];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-milestone]');
      if (fresh) swapContents(wrapper, fresh.innerHTML);
    })
    .catch((error) => console.error(error))
    .finally(() => holdCheckout(false));
});
